import { type Asset, type AssetKind, formatTimecode } from "@cva/shared";
import { useQuery } from "@tanstack/react-query";
import {
  ExternalLink,
  FileImage,
  FolderOpen,
  ImagePlus,
  Layers,
  ListPlus,
  Loader2,
  Music,
  Search,
  Upload,
} from "lucide-react";
import { useRef, useState } from "react";
import { FolderNav, useFolderBrowser } from "../components/media-folders";
import { ContextMenuPopup, useContextMenu } from "../components/ui/context-menu";
import { DropdownItem, DropdownSeparator } from "../components/ui/dropdown";
import { orpc } from "../lib/api";
import { cn } from "../lib/utils";
import { addAsset } from "./commands";
import { toAssetInfo, useEnv } from "./env";
import { type MediaFilter, useLeftNav } from "./left-nav";
import { PanelTitle } from "./PanelTitle";
import { useEditor, useEditorApi } from "./store";
import { UploadingTiles } from "./Uploads";
import { addClipFromAsset, addOverlayFromAsset } from "./video/commands";
import { useVideoApi } from "./video/store";

const KIND_LABELS: Record<AssetKind, string> = {
  video: "Videos",
  image: "Bilder",
  svg: "Grafiken",
  audio: "Audio",
};

const DESIGN_KINDS: AssetKind[] = ["image", "svg"];
const VIDEO_KINDS: AssetKind[] = ["video", "image", "svg", "audio"];

const ACCEPT: Record<AssetKind, string> = {
  image: "image/jpeg,image/png,image/webp,image/gif,image/avif",
  svg: "image/svg+xml",
  video: "video/*",
  audio: "audio/*",
};

const MIME_PREFIX: Record<AssetKind, (mime: string) => boolean> = {
  image: (mime) => mime.startsWith("image/") && mime !== "image/svg+xml",
  svg: (mime) => mime === "image/svg+xml",
  video: (mime) => mime.startsWith("video/"),
  audio: (mime) => mime.startsWith("audio/"),
};

function duration(asset: Asset) {
  return asset.durationMs ? formatTimecode(asset.durationMs, false) : "";
}

/** Short state of a video that is still being prepared. */
function preparingLabel(asset: Asset) {
  if (asset.status === "processing") return "wird verarbeitet";
  const step = asset.media?.step;
  const progress = asset.media?.progress ? ` ${asset.media.progress} %` : "";
  if (step === "proxy") return `Vorschau${progress}`;
  if (step === "filmstrip") return "Filmstreifen";
  if (step === "audio") return "Tonspur";
  return "wird vorbereitet";
}

function isPreparing(asset: Asset) {
  return asset.status === "processing" || asset.media?.status === "processing";
}

/** What a click does, shown under the filter. */
function hint(video: boolean, filter: MediaFilter) {
  if (!video) return null;
  if (filter === "audio")
    return "Klick legt die Datei ab dem Abspielkopf auf die Musikspur. Achte auf die Rechte an der Musik.";
  if (filter === "svg") return "Klick legt die Grafik als Element über das Video.";
  return "Klick hängt an die Hauptspur an, „+ Spur“ legt es darüber (B-Roll, Bild im Bild). Oder in die Zeitleiste ziehen.";
}

/**
 * One library for everything uploaded: images and graphics in designs; videos,
 * images, graphics and audio in videos, with a filter instead of separate tabs.
 */
export function MediaLibraryPanel() {
  const env = useEnv();
  const store = useEditorApi();
  const video = useVideoApi();
  const readOnly = useEditor((s) => s.readOnly);
  const kinds = video ? VIDEO_KINDS : DESIGN_KINDS;
  const stored = useLeftNav((s) => s.mediaFilter);
  const setFilter = useLeftNav((s) => s.setMediaFilter);
  const filter: MediaFilter = stored === "all" || kinds.includes(stored) ? stored : "all";
  const shownKinds = filter === "all" ? kinds : [filter];
  const [search, setSearch] = useState("");
  const input = useRef<HTMLInputElement>(null);
  const folders = useFolderBrowser(env.orgId, search);
  const media = useQuery({
    ...orpc.media.list.queryOptions({
      input: {
        orgId: env.orgId,
        kinds: shownKinds,
        folderId: folders.listFolderId,
        search: search || undefined,
        limit: 120,
      },
    }),
    refetchInterval: (query) => (query.state.data?.items.some(isPreparing) ? 4000 : false),
  });
  const items = media.data?.items ?? [];
  const menu = useContextMenu<Asset>();

  const insert = (asset: Asset) => {
    if (readOnly || asset.status !== "ready") return;
    const info = toAssetInfo(asset);
    if (video && asset.kind !== "svg") addClipFromAsset(store, video, info);
    else addAsset(store, info);
  };

  const menuItems = (asset: Asset) => {
    const ready = asset.status === "ready" && !readOnly;
    const info = toAssetInfo(asset);
    return (
      <>
        {ready ? (
          <DropdownItem onSelect={() => insert(asset)}>
            {asset.kind === "audio" ? (
              <Music />
            ) : video && asset.kind !== "svg" ? (
              <ListPlus />
            ) : (
              <ImagePlus />
            )}
            {!video
              ? "Einfügen"
              : asset.kind === "audio"
                ? "Auf die Musikspur (Abspielkopf)"
                : asset.kind === "svg"
                  ? "Als Element einfügen"
                  : "An die Hauptspur anhängen"}
          </DropdownItem>
        ) : null}
        {ready && video && (asset.kind === "video" || asset.kind === "image") ? (
          <DropdownItem onSelect={() => addOverlayFromAsset(store, video, info)}>
            <Layers /> Auf eigene Spur darüber
          </DropdownItem>
        ) : null}
        {ready && video && asset.kind === "image" ? (
          <DropdownItem onSelect={() => addAsset(store, info)}>
            <ImagePlus /> Als Element aufs Bild
          </DropdownItem>
        ) : null}
        {ready ? <DropdownSeparator /> : null}
        <DropdownItem onSelect={() => window.open(asset.url, "_blank", "noopener")}>
          <ExternalLink /> In neuem Tab ansehen
        </DropdownItem>
        <DropdownItem
          onSelect={() =>
            window.open(
              `/o/${env.orgId}/media?q=${encodeURIComponent(asset.name)}&open=${asset.id}`,
              "_blank",
              "noopener",
            )
          }
        >
          <FolderOpen /> In der Mediathek zeigen
        </DropdownItem>
      </>
    );
  };

  const empty =
    folders.folderId && folders.browsing
      ? "In diesem Ordner ist nichts davon."
      : filter === "audio"
        ? "Noch keine Audiodateien (MP3, M4A, WAV …). Lade Musik oder Sprachaufnahmen hoch."
        : video
          ? "Noch nichts hier. Lade Videos, Bilder oder Musik hoch – oder ziehe sie direkt auf die Zeitleiste."
          : "Noch nichts hier. Lade Bilder hoch oder ziehe sie direkt auf die Fläche.";

  return (
    <>
      <PanelTitle
        actions={
          readOnly ? null : (
            <button
              type="button"
              onClick={() => input.current?.click()}
              className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md bg-brand-600 px-2.5 text-xs font-medium text-white hover:bg-brand-700"
            >
              <Upload className="size-3.5" /> Hochladen
            </button>
          )
        }
      >
        Medien
      </PanelTitle>
      <input
        ref={input}
        type="file"
        multiple
        accept={shownKinds.map((kind) => ACCEPT[kind]).join(",")}
        className="hidden"
        onChange={(event) => {
          const files = [...(event.target.files ?? [])];
          if (files.length > 0)
            env.uploadAndInsert(files, {
              folderId: folders.folderId,
              ...(video ? { timeline: {} } : {}),
            });
          event.target.value = "";
        }}
      />
      <div className="space-y-2 border-b border-zinc-100 p-3">
        <label className="flex h-8 items-center gap-2 rounded-md border border-zinc-200 px-2">
          <Search className="size-4 text-zinc-400" />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Mediathek durchsuchen"
            className="w-full bg-transparent text-sm outline-none"
          />
        </label>
        <div role="tablist" className="flex rounded-lg bg-zinc-100 p-0.5">
          {(["all", ...kinds] as MediaFilter[]).map((entry) => (
            <button
              key={entry}
              type="button"
              role="tab"
              aria-selected={filter === entry}
              onClick={() => setFilter(entry)}
              className={cn(
                "flex-auto rounded-md px-1.5 py-1 text-[11px] font-medium whitespace-nowrap text-zinc-600 hover:text-zinc-900",
                filter === entry && "bg-white text-zinc-900 shadow-sm",
              )}
            >
              {entry === "all" ? "Alle" : KIND_LABELS[entry]}
            </button>
          ))}
        </div>
        {hint(Boolean(video), filter) ? (
          <p className="text-[11px] leading-snug text-zinc-500">{hint(Boolean(video), filter)}</p>
        ) : null}
      </div>
      <div className="flex-1 overflow-y-auto p-3">
        <FolderNav browser={folders} kinds={shownKinds} />
        <UploadingTiles
          layout={video ? "tiles" : "rows"}
          accept={(mime) => shownKinds.some((kind) => MIME_PREFIX[kind](mime))}
        />
        {media.data && items.length === 0 && !folders.hasSubfolders ? (
          <div className="flex flex-col items-center gap-2 py-10 text-center text-sm text-zinc-500">
            {filter === "audio" ? (
              <Music className="size-8 text-zinc-300" />
            ) : (
              <FileImage className="size-8 text-zinc-300" />
            )}
            {empty}
          </div>
        ) : null}
        {video ? (
          <div className="grid grid-flow-row-dense grid-cols-2 gap-2">
            {items.map((asset) =>
              asset.kind === "audio" ? (
                <AudioRow
                  key={asset.id}
                  asset={asset}
                  readOnly={readOnly}
                  onInsert={() => insert(asset)}
                  onContextMenu={menu.open(asset)}
                />
              ) : (
                <VisualTile
                  key={asset.id}
                  asset={asset}
                  readOnly={readOnly}
                  onInsert={() => insert(asset)}
                  onContextMenu={menu.open(asset)}
                  onOverlay={
                    asset.kind === "svg"
                      ? undefined
                      : () => addOverlayFromAsset(store, video, toAssetInfo(asset))
                  }
                />
              ),
            )}
          </div>
        ) : (
          <div className="columns-2 gap-2">
            {items
              .filter((asset) => asset.status === "ready")
              .map((asset) => (
                <button
                  key={asset.id}
                  type="button"
                  draggable={!readOnly}
                  onDragStart={(event) => dragAsset(event, asset)}
                  onClick={() => insert(asset)}
                  onContextMenu={menu.open(asset)}
                  title={`${asset.name}${asset.width ? ` · ${asset.width} × ${asset.height} px` : ""}`}
                  className="mb-2 block w-full overflow-hidden rounded-lg border border-zinc-200 bg-[repeating-conic-gradient(#f4f4f5_0%_25%,#fff_0%_50%)] bg-[length:12px_12px] hover:ring-2 hover:ring-brand-400"
                >
                  <img
                    src={asset.thumbnailUrl ?? asset.url}
                    alt={asset.name}
                    className="block w-full"
                    loading="lazy"
                    draggable={false}
                  />
                </button>
              ))}
          </div>
        )}
      </div>
      <ContextMenuPopup at={menu.at} onClose={menu.close}>
        {menu.target ? menuItems(menu.target) : null}
      </ContextMenuPopup>
    </>
  );
}

function dragAsset(event: React.DragEvent, asset: Asset) {
  event.dataTransfer.setData("application/x-cva-asset", asset.id);
  event.dataTransfer.effectAllowed = "copy";
}

/** Video, image or graphic in the video editor's library. */
function VisualTile({
  asset,
  readOnly,
  onInsert,
  onOverlay,
  onContextMenu,
}: {
  asset: Asset;
  readOnly: boolean;
  onInsert: () => void;
  onOverlay?: (() => void) | undefined;
  onContextMenu: (event: React.MouseEvent) => void;
}) {
  const ready = asset.status === "ready";
  const graphic = asset.kind === "svg";
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: the menu repeats the tile's buttons
    <div className="group relative aspect-[3/4]" onContextMenu={onContextMenu}>
      <button
        type="button"
        disabled={!ready || readOnly}
        draggable={ready && !readOnly}
        onDragStart={(event) => dragAsset(event, asset)}
        onClick={onInsert}
        title={asset.name}
        className={cn(
          "absolute inset-0 overflow-hidden rounded-lg border border-zinc-200 text-left group-hover:ring-2 group-hover:ring-brand-400 disabled:opacity-60",
          graphic
            ? "bg-[repeating-conic-gradient(#f4f4f5_0%_25%,#fff_0%_50%)] bg-[length:12px_12px]"
            : "bg-zinc-900",
        )}
      >
        {asset.thumbnailUrl || graphic ? (
          <img
            src={asset.thumbnailUrl ?? asset.url}
            alt=""
            loading="lazy"
            draggable={false}
            className={cn(
              "absolute inset-0 size-full",
              graphic ? "object-contain p-3" : "object-cover",
            )}
          />
        ) : null}
        <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent px-1.5 pt-4 pb-1 text-[10px] text-white">
          <span className="block truncate">{asset.name}</span>
          <span className="flex items-center gap-1 text-white/70">
            {asset.kind === "video" ? duration(asset) : asset.kind === "svg" ? "Grafik" : "Bild"}
            {isPreparing(asset) ? (
              <>
                <Loader2 className="size-3 animate-spin" />
                {preparingLabel(asset)}
              </>
            ) : null}
          </span>
        </span>
      </button>
      {ready && !readOnly && onOverlay ? (
        <button
          type="button"
          onClick={onOverlay}
          title="Auf einer eigenen Spur über das Video legen, am Abspielkopf (B-Roll, Bild im Bild)"
          className="absolute top-1 right-1 flex items-center gap-1 rounded-md bg-black/70 px-1.5 py-1 text-[10px] font-medium text-white opacity-0 group-hover:opacity-100 hover:bg-brand-600 focus-visible:opacity-100 [&_svg]:size-3"
        >
          <Layers /> + Spur
        </button>
      ) : null}
    </div>
  );
}

/** Music or voice-over: a full-width row between the tiles. */
function AudioRow({
  asset,
  readOnly,
  onInsert,
  onContextMenu,
}: {
  asset: Asset;
  readOnly: boolean;
  onInsert: () => void;
  onContextMenu: (event: React.MouseEvent) => void;
}) {
  const ready = asset.status === "ready";
  return (
    <button
      type="button"
      disabled={!ready || readOnly}
      draggable={ready && !readOnly}
      onDragStart={(event) => dragAsset(event, asset)}
      onClick={onInsert}
      onContextMenu={onContextMenu}
      title={`${asset.name} – auf die Musikspur`}
      className="col-span-2 flex w-full items-center gap-2 rounded-lg border border-zinc-200 px-2.5 py-2 text-left hover:border-brand-300 hover:bg-brand-50 disabled:opacity-60"
    >
      <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-emerald-100 text-emerald-700">
        <Music className="size-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm text-zinc-800">{asset.name}</span>
        <span className="text-xs text-zinc-500">{duration(asset) || "Audio"}</span>
      </span>
    </button>
  );
}
